/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
} from 'reactstrap';

import {
  findingGridDataRefreshAtom,
  selectedHostsAtom,
  SourceType,
  sourceTypeConfig,
} from './state';

interface UnignoreVulnModalProps {
  finding: any;
  isOpen: boolean;
  toggle: () => void;
}

const UnignoreVulnModal: React.FC<UnignoreVulnModalProps> = ({
  isOpen,
  finding: finding,
  toggle,
}) => {
  const selectedHosts = useAtomValue(selectedHostsAtom);
  const refreshFindingsGridData = useSetAtom(findingGridDataRefreshAtom);

  const [selectedOption, setSelectedOption] = useState('unignore');
  const [findingIdsBySameQid, setFindingIdsBySameQid] = useState<number[]>([]);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const findingId = finding.id;
  const sourceType = finding.source_type;
  const title = finding.name;
  const qid = finding.qid;
  const ruleId = finding.sonarqube_rule;

  // only required for CA and EP Vulnerabilities
  let hostName = finding.host_name;
  if (!hostName && sourceType === SourceType.EP) hostName = finding.endpoint_ip;
  if (!hostName && sourceType === SourceType.CA)
    hostName = finding.cloud_agent_name;

  useEffect(
    () => {
      //   console.log(findingData);
      if (sourceType === SourceType.EP || sourceType === SourceType.CA) {
        // make an api call for this qid and host to check if there are multiple
        //  vulnerabilities by this qid and host
        let url = `findings/get_related`;
        setLoading(true);
        callApi(url, 'GET', { finding_id: findingId })
          .then((res) => {
            if (res.status === 201 || res.status === 200) {
              if (res.data.finding_ids) {
                setFindingIdsBySameQid(res.data.finding_ids);
              } else setFindingIdsBySameQid([]);
            }
          })
          .catch((err) => {
            let res = err.response;
            if (res.status === 401 || res.status === 400) {
              setError(err);
            }
          })
          .finally(() => setLoading(false));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleUnignoreVuln = () => {
    if (!error) {
      // Perform unignore vulnerability logic here
      // make an api call for this qid and host to check if there are multiple
      //  vulnerabilities by this qid and host
      let url = `findings/ignore`;

      let data = {
        action: selectedOption === 'unignore' ? 'unignore' : 'unignore_all',
        company_id: localStorage.getItem('currentCompanyId'),
        source_type: sourceType,
      };

      switch (selectedOption) {
        case 'unignore':
          data['finding_id'] = findingId;
          break;
        case 'unignore_all':
          if (sourceType === 'sast') {
            data['finding_id'] = findingId;
          } else {
            data['qid'] = qid;
          }
          break;
        case 'unignore_for_hosts':
          if (sourceType === 'sast') {
            data['finding_id'] = findingId;
          } else {
            data['qid'] = qid;
          }
          data['host_ids'] = selectedHosts.map((item) => item.id);
          break;
      }
      setLoading(true);
      callApi(url, 'PUT', {}, data)
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            showSuccessToast('Vulnerability UnIgnored Successfully');
            sourceTypeConfig[finding.source_type].onIgnoreVuln();
            refreshFindingsGridData(Math.random().toString());
            toggle();
          }
        })
        .catch((err) => {
          let res = err.response || err;
          setError(res.data.message);
          showFailureToast(res.data.message);
        })
        .finally(() => setLoading(false));
    }
  };

  const getFindingIdsBySameQid = () => {
    const firstThree = findingIdsBySameQid.slice(0, 3);

    return `${firstThree.join(', ')}${
      findingIdsBySameQid.length > 3 ? '...' : ''
    }`;
  };

  const handleCancel = () => {
    toggle();
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>Unignore Vulnerability</ModalHeader>
      <ModalBody>
        <Row style={{ marginBottom: '15px' }}>
          <Col md="12">
            {' '}
            <label>
              <p>
                <strong>Name: </strong>
                {title}
              </p>
            </label>
          </Col>
          <Col md="6">
            <label>
              <p>
                <strong>Finding ID: </strong>
                {findingId}
              </p>
            </label>
          </Col>
          {sourceType === 'sast' ? (
            <Col md="6">
              <label>
                <p>
                  <strong>Rule ID: </strong>
                  {ruleId}
                </p>
              </label>
            </Col>
          ) : (
            <Col md="6">
              <label>
                <p>
                  <strong>QID: </strong>
                  {qid}
                </p>
              </label>
            </Col>
          )}
        </Row>
        <Row style={{ marginBottom: '15px' }}>
          <Col md="12">
            <section>
              <strong>
                <label style={{ marginBottom: '00px' }}>
                  <p>Unignore Options</p>
                </label>
              </strong>
              <div className="form-check ml-4">
                <input
                  className="form-check-input"
                  type="radio"
                  name="vuln_unignore_options"
                  id="vuln_unignore_options1"
                  value="unignore"
                  checked={selectedOption === 'unignore'}
                  onChange={handleOptionChange}
                />
                {(sourceType === 'endpoint_data' || sourceType === 'ca_data') &&
                findingIdsBySameQid.length > 1 ? (
                  <>
                    <label
                      className="form-check-label"
                      htmlFor="vuln_ignore_options1"
                    >
                      <p>
                        Unignore all instances of this vulnerability for{' '}
                        <strong>{hostName}</strong> host.
                      </p>
                    </label>
                    <label
                      className="text-warning form-check-label"
                      htmlFor="vuln_ignore_options1"
                    >
                      <strong>Warning:</strong> There are{' '}
                      <strong>{findingIdsBySameQid.length}</strong>{' '}
                      vulnerabilities with the same QID for the same Host (i.e.,
                      with Finding IDs: {getFindingIdsBySameQid()}). Un-ignoring
                      one of these will result in all being unignored.
                    </label>
                  </>
                ) : (
                  <label
                    className="form-check-label"
                    htmlFor="vuln_ignore_options1"
                  >
                    <p>Unignore selected instance of this vulnerability.</p>
                  </label>
                )}
              </div>
              {selectedHosts?.length > 0 && (
                <div className="form-check  ml-4">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="vuln_unignore_options"
                    id="vuln_unignore_options2"
                    value="unignore_for_hosts"
                    checked={selectedOption === 'unignore_for_hosts'}
                    onChange={handleOptionChange}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="vuln_unignore_options2"
                  >
                    <p>
                      Unignore all instances of this vulnerability for selected
                      hosts.
                    </p>
                  </label>
                </div>
              )}
              {atob(localStorage.getItem(btoa('approle')) || btoa('client')) ===
                'admin' && (
                <div className="form-check ml-4">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="vuln_unignore_options"
                    id="vuln_unignore_options3"
                    value="unignore_all"
                    checked={selectedOption === 'unignore_all'}
                    onChange={handleOptionChange}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="vuln_unignore_options3"
                  >
                    <p>
                      Unignore all instances of this vulnerability for all
                      hosts.
                    </p>
                  </label>
                </div>
              )}
            </section>
          </Col>
          <Col md="12" className="spaced">
            {error && (
              <label className="text-danger">
                <strong>Error:</strong> An error occurred while attempting to
                unignore. Unable to proceed.
              </label>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={!!loading}>
          Cancel
        </Button>
        {loading ? (
          <CircleSpinner size={20} color="#0665a9" loading={true} />
        ) : (
          <Button
            color="primary"
            disabled={!!error}
            onClick={handleUnignoreVuln}
          >
            Unignore
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default UnignoreVulnModal;
